/**
 * @file pages/FeaturesPage.js
 * @description Public features page. Three sections: Shipped, In Progress, Coming Soon.
 * Data lives in apps/web/src/data/features.json so non-engineers can edit.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge } from '../design-system';
import featuresData from '../data/features.json';
import PublicTopNav from '../components/PublicTopNav';
import PublicFooter from '../components/PublicFooter';

const SECTIONS = [
  {
    key: 'shipped',
    title: 'Shipped',
    emoji: '🚀',
    badgeColor: 'green',
    badgeLabel: 'Shipped',
    intro: 'Live in production. Available right now.',
  },
  {
    key: 'inProgress',
    title: 'In Progress',
    emoji: '🔨',
    badgeColor: 'yellow',
    badgeLabel: 'In Progress',
    intro: 'Being actively built. Ships within weeks.',
  },
  {
    key: 'comingSoon',
    title: 'Coming Soon',
    emoji: '💡',
    badgeColor: 'blue',
    badgeLabel: 'Coming Soon',
    intro: 'Planned and scoped. On the roadmap.',
  },
];

function formatShippedDate(raw) {
  if (!raw) return null;
  // raw is "YYYY-MM"; render "Mon YYYY"
  const [year, month] = raw.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = parseInt(month, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > 11) return raw;
  return `${months[idx]} ${year}`;
}

function FeaturesPage() {
  return (
    <div style={{ backgroundColor: '#FFFFFF', color: '#111827', minHeight: '100vh' }}>
      <PublicTopNav />

      {/* Hero */}
      <header style={{ textAlign: 'center', padding: '3rem 1.5rem 1.5rem', maxWidth: 820, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em', marginBottom: '0.75rem' }}>
          Features
        </h1>
        <p style={{ color: '#4B5563', fontSize: '1.0625rem', lineHeight: 1.55, margin: 0 }}>
          What's shipped, what's coming, and what we're building right now.
        </p>
      </header>

      {/* Under-active-development callout */}
      <div
        style={{
          maxWidth: 820,
          margin: '0 auto 3rem',
          padding: '1rem 1.25rem',
          backgroundColor: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderRadius: '12px',
          color: '#92400E',
          fontSize: '0.9375rem',
          lineHeight: 1.55,
          marginLeft: '1.5rem',
          marginRight: '1.5rem',
        }}
      >
        <span role="img" aria-label="hammer" style={{ marginRight: '0.5rem' }}>🔨</span>
        <strong>Under active development.</strong> New features ship weekly. Check the{' '}
        <Link to="/build-log" style={{ color: '#92400E', fontWeight: 600, textDecoration: 'underline' }}>
          Build Log
        </Link>{' '}
        for the full play-by-play.
      </div>

      {/* Sections */}
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>
        {SECTIONS.map((section) => {
          const items = featuresData[section.key] || [];
          return (
            <section key={section.key} style={{ marginBottom: '3.5rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.625rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span role="img" aria-label={section.title}>{section.emoji}</span>
                  {section.title}
                </h2>
                <p style={{ color: '#6B7280', fontSize: '0.9375rem', margin: '0.375rem 0 0' }}>
                  {section.intro}
                </p>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem',
                }}
              >
                {items.map((item) => (
                  <Card key={item.name} variant="outline" padding="md">
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, margin: 0 }}>{item.name}</h3>
                      <Badge color={section.badgeColor} variant="subtle" size="sm">
                        {section.badgeLabel}
                      </Badge>
                    </div>
                    <p style={{ color: '#4B5563', fontSize: '0.875rem', lineHeight: 1.55, margin: 0 }}>
                      {item.desc}
                    </p>
                    {item.shippedDate && (
                      <p style={{ color: '#9CA3AF', fontSize: '0.75rem', marginTop: '0.75rem', margin: '0.75rem 0 0' }}>
                        Shipped {formatShippedDate(item.shippedDate)}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            </section>
          );
        })}
      </main>

      <PublicFooter />
    </div>
  );
}

export default FeaturesPage;
